import { BrowseID } from "@/models/browse";
import { MODULE } from "@/config/modules";
import { AuthService } from "@/services/auth-service";
import { i18n } from "@/setup/i18n-setup";

export interface ISidebarTemplate {
	key:string;
	browseIds:BrowseID[];
	blocks:string[];
}

export interface ISidebarBlockDefinition {
	key:string;
	icon:string;
	browseIds:BrowseID[];
	module?:MODULE;
	component:()=>Promise<any>;
}

const CONTACT_BROWSES = [
	BrowseID.CLIENTS,
	BrowseID.SUPPLIERS,
	BrowseID.PROSPECTS
];

const DOCUMENT_BROWSES = [
	BrowseID.SALES,
	BrowseID.PURCHASE_DOCUMENTS,
	BrowseID.PROSPECT_DOCUMENTS
];

const ATTACHMENT_BROWSES = [
	BrowseID.CLIENTS,
	BrowseID.SUPPLIERS,
	BrowseID.PROSPECTS,
	BrowseID.PRODUCTS,
	BrowseID.SALES,
	BrowseID.PURCHASE_DOCUMENTS,
	BrowseID.PROSPECT_DOCUMENTS
];

const EXTRA_FIELD_BROWSES = [
	BrowseID.CLIENTS,
	BrowseID.SUPPLIERS,
	BrowseID.PROSPECTS,
	BrowseID.PRODUCTS,
	BrowseID.SALES,
	BrowseID.PURCHASE_DOCUMENTS,
	BrowseID.PROSPECT_DOCUMENTS,
	BrowseID.CRM
];

export const SIDEBAR_BLOCK_DEFINITIONS:ISidebarBlockDefinition[] = [
	{
		key: "product-image",
		icon: "image",
		browseIds: [BrowseID.PRODUCTS],
		component: ()=>import("@/components/browse/sidebar/SidebarProductImageBlock.vue")
	},
	{
		key: "product-prices",
		icon: "euro-sign",
		browseIds: [BrowseID.PRODUCTS],
		component: ()=>import("@/components/browse/sidebar/SidebarProductPricesBlock.vue")
	},
	{
		key: "product-stock",
		icon: "cubes",
		browseIds: [BrowseID.PRODUCTS],
		module: MODULE.VOORRAADBEHEER,
		component: ()=>import("@/components/browse/sidebar/SidebarProductStockBlock.vue")
	},
	{
		key: "product-size-stock",
		icon: "ruler-horizontal",
		browseIds: [BrowseID.PRODUCTS],
		module: MODULE.MATENTABEL,
		component: ()=>import("@/components/browse/sidebar/SidebarProductSizeStockBlock.vue")
	},
	{
		key: "product-suppliers",
		icon: "truck",
		browseIds: [BrowseID.PRODUCTS],
		component: ()=>import("@/components/browse/sidebar/SidebarProductSuppliersBlock.vue")
	},
	{
		key: "contact-details",
		icon: "address-card",
		browseIds: CONTACT_BROWSES,
		component: ()=>import("@/components/browse/sidebar/SidebarContactDetailsBlock.vue")
	},
	{
		key: "contact-stats",
		icon: "chart-line",
		browseIds: CONTACT_BROWSES,
		component: ()=>import("@/components/browse/sidebar/SidebarContactStatsBlock.vue")
	},
	{
		key: "contact-documents",
		icon: "file-invoice",
		browseIds: CONTACT_BROWSES,
		component: ()=>import("@/components/browse/sidebar/SidebarContactDocumentsBlock.vue")
	},
	{
		key: "sale-rows",
		icon: "list-ol",
		browseIds: DOCUMENT_BROWSES,
		component: ()=>import("@/components/browse/sidebar/SidebarSaleRowsBlock.vue")
	},
	{
		key: "attachments",
		icon: "paperclip",
		browseIds: ATTACHMENT_BROWSES,
		component: ()=>import("@/components/browse/sidebar/SidebarAttachmentsBlock.vue")
	},
	{
		key: "crm-overview",
		icon: "clipboard-list",
		browseIds: [BrowseID.CRM],
		component: ()=>import("@/components/browse/sidebar/SidebarCrmOverviewBlock.vue")
	},
	{
		key: "crm-contact",
		icon: "address-card",
		browseIds: [BrowseID.CRM],
		component: ()=>import("@/components/browse/sidebar/SidebarCrmContactBlock.vue")
	},
	{
		key: "crm-history",
		icon: "history",
		browseIds: [BrowseID.CRM],
		component: ()=>import("@/components/browse/sidebar/SidebarCrmHistoryBlock.vue")
	},
	{
		key: "crm-files",
		icon: "paperclip",
		browseIds: [BrowseID.CRM],
		component: ()=>import("@/components/browse/sidebar/SidebarCrmFilesBlock.vue")
	},
	{
		key: "extra-fields",
		icon: "list",
		browseIds: EXTRA_FIELD_BROWSES,
		module: MODULE.EXTRA_VELDEN,
		component: ()=>import("@/components/browse/sidebar/SidebarExtraFieldsBlock.vue")
	}
];

export const SIDEBAR_TEMPLATES:ISidebarTemplate[] = [
	{
		key: "contact",
		browseIds: CONTACT_BROWSES,
		blocks: ["contact-details", "contact-stats", "contact-documents", "attachments", "extra-fields"]
	},
	{
		key: "product",
		browseIds: [BrowseID.PRODUCTS],
		blocks: ["product-image", "product-prices", "product-stock", "product-size-stock", "product-suppliers", "extra-fields"]
	},
	{
		key: "document",
		browseIds: DOCUMENT_BROWSES,
		blocks: ["sale-rows", "attachments", "extra-fields"]
	},
	{
		key: "crm",
		browseIds: [BrowseID.CRM],
		blocks: ["crm-overview", "crm-contact", "crm-history", "crm-files", "extra-fields"]
	}
];

export function getSidebarTemplatesForBrowse(browseId:number):ISidebarTemplate[] {
	let available = getSidebarBlockDefinitionsForBrowse(browseId).map(d=>d.key);
	return SIDEBAR_TEMPLATES.filter(t=>t.browseIds.indexOf(browseId) != -1).map(t=>{
		return {...t, blocks: t.blocks.filter(b=>available.indexOf(b) != -1)};
	}).filter(t=>t.blocks.length > 0);
}

export function getSidebarTemplateName(key:string):string {
	return i18n.t(`components.browse.sidebar.templates.${key}`).toString();
}

export function getSidebarBlockDefinition(key:string):ISidebarBlockDefinition | undefined {
	return SIDEBAR_BLOCK_DEFINITIONS.find(d=>d.key == key);
}

export function getSidebarBlockDefinitionsForBrowse(browseId:number):ISidebarBlockDefinition[] {
	return SIDEBAR_BLOCK_DEFINITIONS.filter(d=>{
		if (d.browseIds.length > 0 && d.browseIds.indexOf(browseId) == -1) return false;
		if (d.module && !AuthService.hasModule(d.module)) return false;
		return true;
	});
}

export function getSidebarBlockName(key:string):string {
	return i18n.t(`components.browse.sidebar.blocks.${key}.name`).toString();
}

export function getSidebarBlockDescription(key:string):string {
	return i18n.t(`components.browse.sidebar.blocks.${key}.description`).toString();
}
